import * as React from 'react';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Search,
  Plus,
  MapPin,
  Navigation,
  Loader2,
  UserPlus,
  Users,
  Map,
  List,
} from 'lucide-react';
import InteractiveMap from '../../../application/components/InteractiveMap/InteractiveMap.view';
import { Area } from '../../../application/components/InteractiveMap/InteractiveMap.types';
import {
  fetchServiceAreas,
  updateArea,
  UpdateAreaInput,
  createNewArea,
  CreateAreaInput,
  deleteArea,
  updateAreaAssignments,
} from '../../../application/services/areaService';
import AreaAssignmentModal from '../../../application/routes/map/components/AreaAssignmentModal';
import { usePermissions } from '../../../application/context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../../application/context/PermissionsContext/permissionTypes';
import useOrgData from '../../../application/hooks/useOrgData';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ConfirmModal } from '../../components/ui/confirm-modal';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';
import { useIsMobile } from '../../hooks/use-mobile';

declare const global: {
  me: {
    id: string;
    orgID: string;
    isAdmin?: boolean;
    functionalRoles?: string[];
    canCreateAreas?: boolean;
    name?: string;
    imageName?: string;
  };
};

// ── Area Card Component ─────────────────────────────────────

interface AreaCardProps {
  area: Area;
  isSelected: boolean;
  isEditing: boolean;
  editingName: string;
  onSelect: (area: Area) => void;
  onNameChange: (name: string) => void;
  onAssignmentClick: (area: Area) => void;
}

const AreaCard = React.memo(function AreaCard({
  area,
  isSelected,
  isEditing,
  editingName,
  onSelect,
  onNameChange,
  onAssignmentClick,
}: AreaCardProps) {
  const totalUsers = (area.users?.length || 0) + (area.circles?.length || 0);

  return (
    <div
      className={cn(
        'rounded-xl border p-4 cursor-pointer transition-colors',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/40'
      )}
      onClick={() => onSelect(area)}
    >
      <div className="flex gap-2 items-start">
        <MapPin className="size-5 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <Input
              value={editingName}
              onChange={(e) => onNameChange(e.target.value)}
              className="h-8 text-sm font-bold"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold text-foreground truncate">
                {area.name}
              </p>
              <p className="text-xs text-muted-foreground">
                {area.coords?.length || 0} Points
              </p>
            </div>
          )}

          {!isEditing && totalUsers > 0 && (
            <div className="flex items-center gap-2 mt-3">
              <div className="flex -space-x-2">
                {(area.users || []).slice(0, 2).map((user) => (
                  <div
                    key={user.id}
                    className="size-6 rounded-full bg-primary/80 flex items-center justify-center text-[10px] font-semibold text-white border-2 border-background"
                  >
                    {user.name?.[0]?.toUpperCase() || '?'}
                  </div>
                ))}
              </div>
              <span className="text-xs text-muted-foreground">
                {totalUsers} user{totalUsers !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {!isEditing && (
          <button
            className="shrink-0 text-primary hover:text-primary/80 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onAssignmentClick(area);
            }}
            title="Manage assignments"
          >
            <UserPlus className="size-5" />
          </button>
        )}
      </div>
    </div>
  );
});

// ── Main MapPage Component ──────────────────────────────────

export function MapPage() {
  const [areas, setAreas] = useState<Area[]>([]);
  const [selectedAreaId, setSelectedAreaId] = useState<string | undefined>();
  const [isLoadingAreas, setIsLoadingAreas] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [userLocation, setUserLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [mapCenter, setMapCenter] = useState({
    longitude: -122.4194,
    latitude: 37.7749,
    zoom: 13,
  });
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingAreaId, setEditingAreaId] = useState<string | undefined>();
  const [isUpdatingArea, setIsUpdatingArea] = useState(false);
  const [editingAreaName, setEditingAreaName] = useState('');

  // Area creation states
  const [drawingCoords, setDrawingCoords] = useState<
    Array<{ lat: number; lon: number }>
  >([]);
  const [pendingAreaId, setPendingAreaId] = useState<string | undefined>();
  const [isCreatingNewArea, setIsCreatingNewArea] = useState(false);

  // Area assignment modal states
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [selectedAreaForAssignment, setSelectedAreaForAssignment] =
    useState<Area | null>(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Area | null>(null);

  // Mobile view toggle
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState<'map' | 'list'>('map');

  // Refs
  const locationRequestedRef = useRef(false);
  const userHasSelectedAreaRef = useRef(false);
  const mapFlyToRef = useRef<
    ((longitude: number, latitude: number, zoomLevel?: number) => void) | null
  >(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  // Hooks
  const { orgData } = useOrgData();
  const { isSuperAdmin, hasPermission, checkPermission } = usePermissions();
  const toast = useToast();

  // ── Permission helpers ──────────────────────────────────────

  const checkCanCreateAreas = useCallback((): boolean => {
    if (isSuperAdmin()) {
      return true;
    }
    const createPermission = checkPermission(
      PermissionKeys.Territory_CreateArea
    );
    if (
      createPermission &&
      createPermission !== 'none' &&
      createPermission !== null
    ) {
      return true;
    }
    const currentUser = (global as any)?.me;
    if (!currentUser) {
      return false;
    }
    if (currentUser.isAdmin) {
      return true;
    }
    if (
      currentUser.functionalRoles &&
      Array.isArray(currentUser.functionalRoles)
    ) {
      const hasManagerRole = currentUser.functionalRoles.some((role: string) =>
        role.toLowerCase().includes('manager')
      );
      if (hasManagerRole) {
        return true;
      }
    }
    if (currentUser.canCreateAreas === true) {
      return true;
    }
    return false;
  }, [checkPermission, isSuperAdmin]);

  const checkCanEditAreas = useCallback(
    (area?: Area): boolean => {
      if (isSuperAdmin()) {
        return true;
      }
      const userIds = area?.createdBy ? [area.createdBy] : [];
      return hasPermission(PermissionKeys.Territory_UpdateArea, userIds);
    },
    [hasPermission, isSuperAdmin]
  );

  const checkCanDeleteAreas = useCallback(
    (area?: Area): boolean => {
      if (isSuperAdmin()) {
        return true;
      }
      const userIds = area?.createdBy ? [area.createdBy] : [];
      return hasPermission(PermissionKeys.Territory_DeleteArea, userIds);
    },
    [hasPermission, isSuperAdmin]
  );

  const canCreateAreas = checkCanCreateAreas();
  const canEditAreas = checkCanEditAreas();

  // ── Data loading ────────────────────────────────────────────

  useEffect(() => {
    if (!locationRequestedRef.current) {
      locationRequestedRef.current = true;
      getCurrentLocation();
    }
    loadAreas();
  }, []);

  const loadAreas = async (): Promise<void> => {
    try {
      setIsLoadingAreas(true);
      const fetchedAreas = await fetchServiceAreas();
      setAreas(fetchedAreas);
      if (fetchedAreas.length === 0) {
        toast.info('No areas found for this organization.');
      }
    } catch (error: any) {
      console.error('Failed to load areas:', error);
      const errorMessage =
        error?.message ||
        error?.errors?.[0]?.message ||
        'Please check your network connection and try again.';
      toast.error('Failed to load areas.', errorMessage);
    } finally {
      setIsLoadingAreas(false);
    }
  };

  // ── Location helpers ────────────────────────────────────────

  const getCurrentLocation = () => {
    setIsLoadingLocation(true);
    if (!navigator.geolocation) {
      setIsLoadingLocation(false);
      return;
    }
    if (
      window.location.protocol !== 'https:' &&
      window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1'
    ) {
      setIsLoadingLocation(false);
      return;
    }
    requestLocation();
  };

  const requestLocation = () => {
    const options = {
      enableHighAccuracy: false,
      timeout: 20000,
      maximumAge: 0,
    };
    setTimeout(() => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setUserLocation(location);

          // Only fly to user location if they haven't already selected an area
          if (!userHasSelectedAreaRef.current) {
            if (mapFlyToRef.current) {
              mapFlyToRef.current(location.longitude, location.latitude, 15);
            } else {
              setMapCenter({
                longitude: location.longitude,
                latitude: location.latitude,
                zoom: 15,
              });
            }
          }
          setIsLoadingLocation(false);
        },
        () => {
          setIsLoadingLocation(false);

          // Only set default center if user hasn't selected an area
          if (!userHasSelectedAreaRef.current) {
            const defaultCenter = {
              longitude: -122.4194,
              latitude: 37.7749,
              zoom: 13,
            };
            if (mapFlyToRef.current) {
              mapFlyToRef.current(
                defaultCenter.longitude,
                defaultCenter.latitude,
                13
              );
            } else {
              setMapCenter(defaultCenter);
            }
          }
        },
        options
      );
    }, 100);
  };

  const centerOnUser = () => {
    if (userLocation) {
      if (mapFlyToRef.current) {
        mapFlyToRef.current(userLocation.longitude, userLocation.latitude, 15);
      } else {
        setMapCenter({
          longitude: userLocation.longitude,
          latitude: userLocation.latitude,
          zoom: 15,
        });
      }
      setSelectedAreaId(undefined);
      toast.success('Centered on your location');
    } else {
      setIsLoadingLocation(true);
      if (!navigator.geolocation) {
        setIsLoadingLocation(false);
        toast.info('Geolocation is not supported by this browser.');
        return;
      }
      const options = {
        enableHighAccuracy: false,
        timeout: 20000,
        maximumAge: 0,
      };
      setTimeout(() => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };
            setUserLocation(location);
            if (mapFlyToRef.current) {
              mapFlyToRef.current(location.longitude, location.latitude, 15);
            } else {
              setMapCenter({
                longitude: location.longitude,
                latitude: location.latitude,
                zoom: 15,
              });
            }
            setIsLoadingLocation(false);
            toast.success('Centered on your location');
          },
          (error) => {
            setIsLoadingLocation(false);
            if (mapFlyToRef.current) {
              mapFlyToRef.current(-122.4194, 37.7749, 13);
            } else {
              setMapCenter({
                longitude: -122.4194,
                latitude: 37.7749,
                zoom: 13,
              });
            }
            let msg = 'Unable to get location.';
            if (error.code === error.PERMISSION_DENIED) {
              msg = 'Location access was denied.';
            } else if (error.code === error.TIMEOUT) {
              msg = 'Location request timed out.';
            }
            toast.info(msg);
          },
          options
        );
      }, 100);
    }
  };

  // ── Area selection & map interaction ────────────────────────

  const handleAreaSelect = useCallback(
    (area: Area) => {
      setSelectedAreaId(area.id);
      userHasSelectedAreaRef.current = true;
      if (area.coords && area.coords.length > 0) {
        const centerLat =
          area.coords.reduce((sum, coord) => sum + coord.lat, 0) /
          area.coords.length;
        const centerLon =
          area.coords.reduce((sum, coord) => sum + coord.lon, 0) /
          area.coords.length;
        if (mapFlyToRef.current) {
          mapFlyToRef.current(centerLon, centerLat, 14);
        } else {
          setMapCenter({ longitude: centerLon, latitude: centerLat, zoom: 14 });
        }
      }
      if (isMobile) {
        setMobileView('map');
      }
    },
    [isMobile]
  );

  const handleAreaClick = useCallback((area: Area) => {
    setSelectedAreaId(area.id);
    userHasSelectedAreaRef.current = true;

    // Center map on the clicked area
    if (area.coords && area.coords.length > 0) {
      const centerLat =
        area.coords.reduce((sum, coord) => sum + coord.lat, 0) /
        area.coords.length;
      const centerLon =
        area.coords.reduce((sum, coord) => sum + coord.lon, 0) /
        area.coords.length;

      if (mapFlyToRef.current) {
        mapFlyToRef.current(centerLon, centerLat, 14);
      } else {
        setMapCenter({
          longitude: centerLon,
          latitude: centerLat,
          zoom: 14,
        });
      }
    }
  }, []);

  const handleMapReady = useCallback(
    (
      flyToFunction: (
        longitude: number,
        latitude: number,
        zoomLevel?: number
      ) => void
    ) => {
      mapFlyToRef.current = flyToFunction;
    },
    []
  );

  // ── Area editing ────────────────────────────────────────────

  const handleAreaEdit = async (
    areaId: string,
    updates: Partial<UpdateAreaInput>
  ) => {
    try {
      setIsUpdatingArea(true);
      const originalArea = areas.find((area) => area.id === areaId);
      const updatedArea = await updateArea(
        { id: areaId, ...updates },
        originalArea
      );
      setAreas((prevAreas) =>
        prevAreas.map((area) => (area.id === areaId ? updatedArea : area))
      );
      toast.success(`Successfully updated ${updatedArea.name}`);
      setIsEditMode(false);
      setEditingAreaId(undefined);
    } catch (error: any) {
      console.error('Failed to update area:', error);
      const errorMessage =
        error?.message ||
        error?.errors?.[0]?.message ||
        'Please check your network connection and try again.';
      toast.error('Failed to update area.', errorMessage);
    } finally {
      setIsUpdatingArea(false);
    }
  };

  const handleAreaDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    try {
      setIsUpdatingArea(true);
      await deleteArea(deleteTarget.id);
      setAreas((prevAreas) =>
        prevAreas.filter((area) => area.id !== deleteTarget.id)
      );
      if (selectedAreaId === deleteTarget.id) {
        setSelectedAreaId(undefined);
      }
      toast.success(`Successfully deleted ${deleteTarget.name}`);
      setDeleteTarget(null);
    } catch (error: any) {
      console.error('Failed to delete area:', error);
      const errorMessage =
        error?.message ||
        error?.errors?.[0]?.message ||
        'Please check your network connection and try again.';
      toast.error('Failed to delete area.', errorMessage);
    } finally {
      setIsUpdatingArea(false);
    }
  };

  const startEditingArea = (areaId: string) => {
    const area = areas.find((a) => a.id === areaId);
    if (!checkCanEditAreas(area)) {
      toast.error('You do not have permission to edit this area.');
      return;
    }
    setIsEditMode(true);
    setEditingAreaId(areaId);
    setSelectedAreaId(areaId);
    setEditingAreaName(area?.name || '');
    if (isMobile) {
      setMobileView('map');
    }
    toast.info(
      'Edit mode active. Click map to add points, drag to move, right-click to delete.'
    );
  };

  const cancelEditingArea = () => {
    if (pendingAreaId === editingAreaId) {
      setAreas((prev) => prev.filter((area) => area.id !== editingAreaId));
      setPendingAreaId(undefined);
      setIsCreatingNewArea(false);
      setDrawingCoords([]);
    }
    setIsEditMode(false);
    setEditingAreaId(undefined);
    setEditingAreaName('');
    setSelectedAreaId(undefined);
    if (pendingAreaId !== editingAreaId) {
      loadAreas();
    }
  };

  const saveAreaChanges = async () => {
    if (!editingAreaId) {
      return;
    }
    const editingArea = areas.find((area) => area.id === editingAreaId);
    if (!editingArea) {
      return;
    }

    if (pendingAreaId === editingAreaId) {
      // Creating a new area
      try {
        setIsUpdatingArea(true);
        const createAreaInput: CreateAreaInput = {
          name: editingAreaName.trim() || editingArea.name,
          coords: editingArea.coords,
        };
        const newArea = await createNewArea(createAreaInput);
        const currentUser = (global as any)?.me;
        const areaWithCreator: Area = {
          ...newArea,
          createdBy: newArea.createdBy || currentUser?.id,
          lastUpdatedBy: newArea.lastUpdatedBy || currentUser?.id,
        };
        setAreas((prev) => {
          const updatedAreas = prev.map((area) =>
            area.id === editingAreaId ? areaWithCreator : area
          );
          return updatedAreas.sort((a, b) => a.name.localeCompare(b.name));
        });
        setPendingAreaId(undefined);
        setIsCreatingNewArea(false);
        setDrawingCoords([]);
        setIsEditMode(false);
        setEditingAreaId(undefined);
        setEditingAreaName('');
        setSelectedAreaId(areaWithCreator.id);
        toast.success(`Successfully created ${areaWithCreator.name}`);
      } catch (error: any) {
        console.error('Failed to create area:', error);
        const errorMessage =
          error?.message ||
          'Please check your network connection and try again.';
        toast.error('Failed to create area.', errorMessage);
      } finally {
        setIsUpdatingArea(false);
      }
    } else {
      // Updating existing area
      const updates: Partial<UpdateAreaInput> = {
        coords: editingArea.coords,
      };
      if (editingAreaName.trim() && editingAreaName !== editingArea.name) {
        updates.name = editingAreaName.trim();
      }
      await handleAreaEdit(editingAreaId, updates);
    }
  };

  // ── Coordinate handlers ─────────────────────────────────────

  const handleCoordinateAdd = useCallback(
    (areaId: string, coordinate: { lat: number; lon: number }) => {
      setAreas((prevAreas) =>
        prevAreas.map((area) =>
          area.id === areaId
            ? { ...area, coords: [...area.coords, coordinate] }
            : area
        )
      );
    },
    []
  );

  const handleCoordinateMove = useCallback(
    (
      areaId: string,
      coordinateIndex: number,
      newCoordinate: { lat: number; lon: number }
    ) => {
      setAreas((prevAreas) =>
        prevAreas.map((area) =>
          area.id === areaId
            ? {
                ...area,
                coords: area.coords.map((coord, index) =>
                  index === coordinateIndex ? newCoordinate : coord
                ),
              }
            : area
        )
      );
    },
    []
  );

  const handleCoordinateDelete = useCallback(
    (areaId: string, coordinateIndex: number) => {
      setAreas((prevAreas) =>
        prevAreas.map((area) =>
          area.id === areaId
            ? {
                ...area,
                coords: area.coords.filter(
                  (_, index) => index !== coordinateIndex
                ),
              }
            : area
        )
      );
      if (isCreatingNewArea && pendingAreaId === areaId) {
        setDrawingCoords((prevCoords) =>
          prevCoords.filter((_, index) => index !== coordinateIndex)
        );
      }
    },
    [isCreatingNewArea, pendingAreaId]
  );

  // ── Area creation ───────────────────────────────────────────

  const handleCreateNewArea = () => {
    if (!canCreateAreas) {
      toast.error('You do not have permission to create areas.');
      return;
    }
    const currentUser = (global as any)?.me;
    const tempAreaId = `temp-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    const tempArea: Area = {
      id: tempAreaId,
      name: '',
      coords: [],
      createdBy: currentUser?.id,
      lastUpdatedBy: currentUser?.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isDeleted: false,
    };
    setAreas((prev) => [tempArea, ...prev]);
    setSelectedAreaId(tempAreaId);
    setEditingAreaId(tempAreaId);
    setEditingAreaName('');
    setIsEditMode(true);
    setPendingAreaId(tempAreaId);
    setIsCreatingNewArea(true);
    setDrawingCoords([]);
    if (isMobile) {
      setMobileView('map');
    }
    toast.info('Click on the map to add boundary points.');
  };

  const handleDrawingClick = useCallback(
    (location: { latitude: number; longitude: number }) => {
      if (isCreatingNewArea && pendingAreaId) {
        const newCoord = { lat: location.latitude, lon: location.longitude };
        const updatedCoords = [...drawingCoords, newCoord];
        setDrawingCoords(updatedCoords);
        setAreas((prev) =>
          prev.map((area) =>
            area.id === pendingAreaId
              ? { ...area, coords: updatedCoords }
              : area
          )
        );
      }
    },
    [isCreatingNewArea, pendingAreaId, drawingCoords]
  );

  const cancelAreaCreation = useCallback(() => {
    if (pendingAreaId) {
      setAreas((prev) => prev.filter((area) => area.id !== pendingAreaId));
      setPendingAreaId(undefined);
    }
    setIsCreatingNewArea(false);
    setDrawingCoords([]);
    setIsEditMode(false);
    setEditingAreaId(undefined);
    setEditingAreaName('');
    setSelectedAreaId(undefined);
    toast.info('Area creation cancelled.');
  }, [pendingAreaId, toast]);

  // ── Keyboard shortcuts ──────────────────────────────────────

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (isCreatingNewArea && event.key === 'Escape') {
        cancelAreaCreation();
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isCreatingNewArea, cancelAreaCreation]);

  // ── Area assignment handlers ────────────────────────────────

  const handleAreaAssignmentClick = useCallback((area: Area) => {
    setSelectedAreaForAssignment(area);
    setAssignmentModalOpen(true);
  }, []);

  const handleUpdateAssignments = async (
    areaId: string,
    users: any[],
    circles: any[]
  ) => {
    const originalArea = areas.find((a) => a.id === areaId);
    if (!originalArea) {
      return;
    }
    const updatedArea = await updateAreaAssignments(
      areaId,
      users,
      circles,
      originalArea
    );
    setAreas((prevAreas) =>
      prevAreas.map((area) => (area.id === areaId ? updatedArea : area))
    );
  };

  const closeAssignmentModal = () => {
    setAssignmentModalOpen(false);
    setSelectedAreaForAssignment(null);
  };

  // ── Auto-scroll to selected area ───────────────────────────

  useEffect(() => {
    if (selectedAreaId && selectedItemRef.current) {
      setTimeout(() => {
        selectedItemRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  }, [selectedAreaId]);

  // ── Filtered areas ──────────────────────────────────────────

  const filteredAreas = useMemo(() => {
    if (!searchTerm.trim()) {
      return areas;
    }
    const searchLower = searchTerm.toLowerCase();
    return areas.filter((area) =>
      area.name.toLowerCase().includes(searchLower)
    );
  }, [areas, searchTerm]);

  // ── Header actions ──────────────────────────────────────────

  const headerActions = useMemo(() => {
    const actions: Array<{
      id: string;
      label: string;
      icon?: React.ReactNode;
      variant?: 'primary' | 'secondary';
      onClick?: () => void;
    }> = [];

    if (canCreateAreas && !isEditMode && !isCreatingNewArea) {
      actions.push({
        id: 'new-area',
        label: 'New Area',
        icon: <Plus className="size-4" />,
        variant: 'primary',
        onClick: handleCreateNewArea,
      });
    }

    return actions;
  }, [canCreateAreas, isEditMode, isCreatingNewArea]);

  // ── Render ──────────────────────────────────────────────────

  if (isLoadingAreas) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Interactive Map" />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="size-10 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading areas...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader title="Interactive Map" actions={headerActions} />

      {/* ── Mobile View Toggle ── */}
      {isMobile && (
        <div className="flex md:hidden border-b border-border bg-background px-4 py-2 gap-2">
          <button
            onClick={() => setMobileView('map')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium transition-colors',
              mobileView === 'map'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            )}
          >
            <Map className="size-4" />
            Map
          </button>
          <button
            onClick={() => setMobileView('list')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium transition-colors',
              mobileView === 'list'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            )}
          >
            <List className="size-4" />
            Areas
          </button>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* ── Sidebar ── */}
        <aside
          className={cn(
            'md:w-[380px] shrink-0 border-r border-border bg-background flex flex-col overflow-hidden',
            isMobile && mobileView !== 'list' ? 'hidden' : '',
            isMobile && mobileView === 'list' ? 'w-full' : ''
          )}
        >
          {/* Sidebar header */}
          <div className="px-6 pt-6 pb-0 space-y-1">
            <h2 className="text-lg font-bold text-foreground tracking-tight">
              Areas ({areas.length})
            </h2>
            <p className="text-sm text-muted-foreground">
              {userLocation
                ? `📍 Location found: ${userLocation.latitude.toFixed(
                    4
                  )}, ${userLocation.longitude.toFixed(4)}`
                : '📍 Using default location (San Francisco)'}
            </p>
          </div>

          {/* Search */}
          <div className="px-6 py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Search Areas"
                className="pl-9 bg-muted border-0 rounded-lg h-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Edit mode / creation banners */}
          {isCreatingNewArea && (
            <div className="px-6 pb-3">
              <div className="rounded-lg bg-primary/10 border border-primary/20 p-3 text-xs text-foreground space-y-1">
                <p className="font-bold">Creating New Area</p>
                <p>
                  Click map to add points ({drawingCoords.length} added). Need
                  3+ points and a name.
                </p>
              </div>
            </div>
          )}

          {isEditMode && editingAreaId && !isCreatingNewArea && (
            <div className="px-6 pb-3">
              <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-3 text-xs text-foreground space-y-1">
                <p className="font-bold">Editing Mode</p>
                <p>Click to add points. Drag to move. Right-click to delete.</p>
              </div>
            </div>
          )}

          {/* Action buttons for edit/create mode */}
          {(isCreatingNewArea || (isEditMode && editingAreaId)) && (
            <div className="px-6 pb-3 flex gap-2">
              {isCreatingNewArea ? (
                <>
                  <Button
                    size="sm"
                    onClick={saveAreaChanges}
                    disabled={
                      isUpdatingArea ||
                      !editingAreaName.trim() ||
                      drawingCoords.length < 3
                    }
                  >
                    {isUpdatingArea ? 'Creating...' : 'Create Area'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelAreaCreation}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    onClick={saveAreaChanges}
                    disabled={isUpdatingArea}
                  >
                    {isUpdatingArea ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelEditingArea}
                    disabled={isUpdatingArea}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Selected area actions (edit/delete) */}
          {!isCreatingNewArea && !isEditMode && selectedAreaId && (
            <div className="px-6 pb-3 flex gap-2">
              {(() => {
                const selectedArea = areas.find((a) => a.id === selectedAreaId);
                return (
                  <>
                    {checkCanEditAreas(selectedArea) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEditingArea(selectedAreaId)}
                      >
                        Edit Area
                      </Button>
                    )}
                    {checkCanDeleteAreas(selectedArea) && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeleteTarget(selectedArea || null)}
                        disabled={isUpdatingArea}
                      >
                        Delete
                      </Button>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Area list */}
          <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-2">
            {filteredAreas.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <MapPin className="size-10 mb-3" />
                <p className="text-sm">
                  {searchTerm ? 'No areas found' : 'No areas available'}
                </p>
              </div>
            ) : (
              filteredAreas.map((area) => (
                <div
                  key={area.id}
                  ref={selectedAreaId === area.id ? selectedItemRef : null}
                >
                  <AreaCard
                    area={area}
                    isSelected={selectedAreaId === area.id}
                    isEditing={isEditMode && editingAreaId === area.id}
                    editingName={editingAreaName}
                    onSelect={handleAreaSelect}
                    onNameChange={setEditingAreaName}
                    onAssignmentClick={handleAreaAssignmentClick}
                  />
                </div>
              ))
            )}
          </div>
        </aside>

        {/* ── Map Container ── */}
        <div
          className={cn(
            'flex-1 relative p-4',
            isMobile && mobileView !== 'map' ? 'hidden' : ''
          )}
        >
          <div className="size-full rounded-2xl overflow-hidden border border-border touch-none">
            <InteractiveMap
              initialViewState={mapCenter}
              drawingEnabled={isCreatingNewArea}
              areas={areas}
              selectedAreaId={selectedAreaId}
              userLocation={userLocation}
              onDrawingClick={
                isCreatingNewArea ? handleDrawingClick : undefined
              }
              drawingCoords={drawingCoords}
              onAreaClick={handleAreaClick}
              isEditMode={isEditMode && canEditAreas}
              editingAreaId={editingAreaId}
              onCoordinateAdd={canEditAreas ? handleCoordinateAdd : undefined}
              onCoordinateMove={canEditAreas ? handleCoordinateMove : undefined}
              onCoordinateDelete={
                canEditAreas ? handleCoordinateDelete : undefined
              }
              mapboxToken={orgData?.configParms?.features?.route?.mapboxKey}
              onMapReady={handleMapReady}
            />
          </div>

          {/* Location button overlay */}
          <button
            onClick={centerOnUser}
            disabled={isLoadingLocation}
            className="absolute top-8 right-8 size-10 rounded-lg border border-border bg-background shadow-sm flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-50"
            title={
              isLoadingLocation
                ? 'Getting your location...'
                : userLocation
                ? 'Center on my location'
                : 'Get my location'
            }
          >
            {isLoadingLocation ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Navigation className="size-4 text-foreground" />
            )}
          </button>

          {/* Mobile floating toolbar for area creation/editing */}
          {isMobile && (isCreatingNewArea || (isEditMode && editingAreaId)) && (
            <div className="absolute bottom-8 left-4 right-4 md:hidden">
              <div className="rounded-xl border border-border bg-background shadow-lg p-4 space-y-3">
                {isCreatingNewArea && (
                  <div className="text-xs text-muted-foreground">
                    Tap map to add points ({drawingCoords.length} added). Need
                    3+ points.
                  </div>
                )}
                {isEditMode && !isCreatingNewArea && (
                  <div className="text-xs text-muted-foreground">
                    Tap to add points. Drag to move. Long-press to delete.
                  </div>
                )}
                {isCreatingNewArea && (
                  <Input
                    placeholder="Area name"
                    value={editingAreaName}
                    onChange={(e) => setEditingAreaName(e.target.value)}
                    className="h-9 text-sm"
                  />
                )}
                {isEditMode && !isCreatingNewArea && (
                  <Input
                    placeholder="Area name"
                    value={editingAreaName}
                    onChange={(e) => setEditingAreaName(e.target.value)}
                    className="h-9 text-sm"
                  />
                )}
                <div className="flex gap-2">
                  {isCreatingNewArea ? (
                    <>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={saveAreaChanges}
                        disabled={
                          isUpdatingArea ||
                          !editingAreaName.trim() ||
                          drawingCoords.length < 3
                        }
                      >
                        {isUpdatingArea ? 'Creating...' : 'Create Area'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={cancelAreaCreation}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={saveAreaChanges}
                        disabled={isUpdatingArea}
                      >
                        {isUpdatingArea ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={cancelEditingArea}
                        disabled={isUpdatingArea}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Delete Confirmation Modal ── */}
      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleAreaDelete}
        title="Delete Area?"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isUpdatingArea}
      />

      {/* ── Area Assignment Modal ── */}
      {assignmentModalOpen && selectedAreaForAssignment && (
        <AreaAssignmentModal
          open={assignmentModalOpen}
          onClose={closeAssignmentModal}
          onUpdateAssignments={handleUpdateAssignments}
          area={selectedAreaForAssignment}
        />
      )}
    </div>
  );
}
